// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The standard CRC-32 (IEEE 802.3 / zlib) that PNG 3rd ed §5.5 specifies,
 * computed over the chunk TYPE bytes followed by the chunk DATA bytes — never
 * the length field.
 *
 * Written first-party rather than pulled in, because the tool needs one
 * property no CRC library offers: the ability to compute a CRC over a range of
 * an existing buffer without slicing it. A 60 MB IDAT sliced to be CRC'd is a
 * 60 MB copy, and this is a tool people run on phones.
 */

const TABLE = new Uint32Array(256);
for (let n = 0; n < 256; n++) {
  let c = n;
  for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  TABLE[n] = c >>> 0;
}

/** CRC-32 over `bytes[start, end)`. Returns an unsigned 32-bit value. */
export function crc32(bytes: Uint8Array, start = 0, end = bytes.length): number {
  let c = 0xffffffff;
  for (let i = start; i < end; i++) c = TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * CRC-32 continued across two ranges, so a chunk's type and data can be CRC'd
 * where they sit rather than being concatenated first.
 */
export function crc32Pair(
  a: Uint8Array,
  aStart: number,
  aEnd: number,
  b: Uint8Array,
  bStart: number,
  bEnd: number,
): number {
  let c = 0xffffffff;
  for (let i = aStart; i < aEnd; i++) c = TABLE[(c ^ a[i]) & 0xff] ^ (c >>> 8);
  for (let i = bStart; i < bEnd; i++) c = TABLE[(c ^ b[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
